import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { SelectedAlertsContext } from "utils/SelectedAlertContext";
import { orLossWeeklyTrendData } from "Samarth Module/api/orltAPI";

// Constants
const CHART_HEIGHT = 500; // Increased from 420 for more vertical space
const CHART_COLORS = {
  planned: "#007BFF",
  produced: "#00C49F",
  or: "#FFC107",
  correctedOr: "#FF4D4F",
};

const LEGEND_CONFIG = [
  { label: "Planned", color: CHART_COLORS.planned },
  { label: "Produced", color: CHART_COLORS.produced },
  { label: "OR %", color: CHART_COLORS.or },
  { label: "Corrected OR %", color: CHART_COLORS.correctedOr },
];

const LegendItem = ({ label, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <Box
      component="span"
      sx={{
        width: "2px",
        height: "14px",
        backgroundColor: color,
        display: "inline-block",
        borderRadius: "1px",
      }}
    />
    <span style={{ fontSize: "11px", fontWeight: 400, color: "#333" }}>
      {label}
    </span>
  </Box>
);

export default function OrTrendChartView({ tblView, selectionType }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { orLossTreeSelectedFilters } = useContext(SelectedAlertsContext);

  // Fetch data
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await orLossWeeklyTrendData(orLossTreeSelectedFilters, tblView, selectionType);
        if (isMounted) {
          // Handle different response structures
          const dataArray = Array.isArray(response) ? response : (response?.data || []);
          setData(dataArray);
        }
      } catch (err) {
        console.error("OR Trends error", err);
        if (isMounted) {
          setData([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [JSON.stringify(orLossTreeSelectedFilters), tblView, selectionType]); // Stringify to prevent reference changes

  // Auto-scroll to the end of chart after data loads
  useEffect(() => {
    if (!loading && data.length > 0) {
      const scrollableDiv = document.getElementById('or_trend_chart');
      if (scrollableDiv) {
        setTimeout(() => {
          scrollableDiv.scrollTo({
            top: 0,
            left: scrollableDiv.scrollWidth,
            behavior: "smooth"
          });
        }, 100);
      }
    }
  }, [data, loading]);

  // Check if data is empty (all zeros except CalendarWeek)
  const isNoData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return true;
    return data.every(item =>
      Object.keys(item).every(key => key === "CalendarWeek" || item[key] === 0)
    );
  }, [data]);

  // Generate chart options
  const chartOptions = useMemo(() => {
    if (!data || data.length === 0) return null;

    const weeks = data.map((item) => item["CalendarWeek"] || "");
    const planned = data.map((item) => parseFloat(item["Planned"]) || 0);
    const produced = data.map((item) => parseFloat(item["Produced"]) || 0);
    const or = data.map((item) => parseFloat(item["OR %"]) || 0);
    const correctedOr = data.map((item) => parseFloat(item["Corrected OR%"]) || 0);

    return {
      chart: {
        zoomType: "xy",
        backgroundColor: "#fff",
        height: CHART_HEIGHT,
        spacingTop: 0,
        spacingBottom: 20,
      },
      title: {
        text: "",
        align: "left",
        style: { fontSize: "14px", fontWeight: "bold" },
        useHTML: true,
      },
      xAxis: {
        categories: weeks,
        crosshair: true,
        lineWidth: 1,
        tickWidth: 0,
      },
      yAxis: [
        {
          title: { text: null },
          labels: { enabled: false },
          gridLineColor: "#999",
          gridLineDashStyle: "Dash",
          gridLineWidth: 1,
          height: "60%",
          top: "40%",
        },
        {
          title: { text: null },
          labels: { enabled: false },
          gridLineWidth: 0,
          lineWidth: 0,
          top: 0,
          height: "35%",
          offset: 0,
        },
      ],
      tooltip: {
        shared: true,
        formatter: function () {
          const pointMap = this.points.reduce((acc, point) => {
            acc[point.series.name] = point.y;
            return acc;
          }, {});
          return `<b>${this.x}</b><br/>
            <span style="color:${CHART_COLORS.planned}">|</span> Planned: <b>${pointMap["Planned"]?.toFixed(2)}T</b><br/>
            <span style="color:${CHART_COLORS.produced}">|</span> Produced: <b>${pointMap["Produced"]?.toFixed(2)}T</b><br/>
            <span style="color:${CHART_COLORS.or}">|</span> OR %: <b>${pointMap["OR %"]?.toFixed(2)}%</b><br/>
            <span style="color:${CHART_COLORS.correctedOr}">|</span> Corrected OR %: <b>${pointMap["Corrected OR%"]?.toFixed(2)}%</b>`;
        },
      },
      legend: { enabled: false },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          groupPadding: 0.2,
          dataLabels: {
            enabled: false,
          },
          borderRadius: 3,
          allowOverlap: true,
        },
        line: {
          marker: { enabled: true, symbol: "circle", radius: 4 },
          dataLabels: { 
            enabled: true, 
            formatter: function() {
              return this.y.toFixed(2) + '%';
            },
            style: { fontSize: "10px" } 
          },
        },
      },
      series: [
        { 
          name: "Planned", 
          type: "column", 
          data: planned, 
          color: CHART_COLORS.planned, 
          yAxis: 0,
          dataLabels: {
            enabled: true,
            formatter: function() {
              const val = this.y;
              const decimalPart = val % 1;
              if (decimalPart === 0) {
                return Math.round(val).toString();
              } else {
                return val.toFixed(1);
              }
            },
            style: { fontSize: "8px", color: "#333", textOutline: "1px white" },
            inside: false,
            align: "center",
            verticalAlign: "top",
            x: -6,
            y: -18,
            crop: false,
            overflow: "allow",
            allowOverlap: true,
          }
        },
        { 
          name: "Produced", 
          type: "column", 
          data: produced, 
          color: CHART_COLORS.produced, 
          yAxis: 0,
          dataLabels: {
            enabled: true,
            formatter: function() {
              const val = this.y;
              const decimalPart = val % 1;
              if (decimalPart === 0) {
                return Math.round(val).toString();
              } else {
                return val.toFixed(1);
              }
            },
            style: { fontSize: "8px", color: "#333", textOutline: "1px white" },
            inside: false,
            align: "center",
            verticalAlign: "top",
            x: 6,
            y: -18,
            crop: false,
            overflow: "allow",
            allowOverlap: true,
          }
        },
        { name: "OR %", type: "line", data: or, color: CHART_COLORS.or, yAxis: 1, zIndex: 10 },
        { name: "Corrected OR%", type: "line", data: correctedOr, color: CHART_COLORS.correctedOr, yAxis: 1, zIndex: 10 },
      ],
      credits: { enabled: false },
    };
  }, [data]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "250px",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  // No data state
  if (isNoData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "270px",
        }}
      >
        Data is not available
      </Box>
    );
  }

  // Calculate min width for horizontal scrolling
  const minWidth = chartOptions.xAxis.categories.length > 6 
    ? chartOptions.xAxis.categories.length * 90 
    : "100%";

  return (
    data && data.length === 0 ?
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "290px",
          marginTop: '10px'
        }}
      >
        Data is not available
      </Box> :
      <>
        <Box id="or_trend_chart" sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth }}>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </Box>
        </Box>

        <Box sx={{
          mt: 0.3,
          pt: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          fontSize: "11px",
          fontWeight: 400
        }}>
          {LEGEND_CONFIG.map((item) => (
            <LegendItem key={item.label} label={item.label} color={item.color} />
          ))}
        </Box>
      </>
  );
}
